import { common } from '../i18n/common.js';
import { pricesData } from '../../data/prices.js';

// Eagerly import all place images and map by base filename (without extension)
const placeImageModules = import.meta.glob('../../public/places/*.webp', {
  eager: true,
  import: 'default',
  query: '?url',
});
const normalizePublicUrl = url =>
  typeof url === 'string' ? url.replace('/public/', '/') : url;
const placeImagesByName = Object.entries(placeImageModules).reduce(
  (acc, [path, url]) => {
    const filename = path.split('/').pop() || '';
    const base = filename.replace(/\.webp$/i, '');
    acc[base] = normalizePublicUrl(url);
    return acc;
  },
  {}
);

// Extract destination info from route string
function parseDestination(route) {
  const routeStr = String(route || '');
  const parts = routeStr.split(/—|–|-| to |→/i);
  const rawDest = (parts[1] || parts[0] || '').trim();
  const title = rawDest.replace(/\(.*?\)/g, '').trim();
  const key = title
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, '_');
  return { title, key };
}

// Short stories by destination key (fallback to generic)
const storiesByKey = {
  city_centre:
    'The historic heart of Prague with cobblestone lanes, Gothic spires and cafes along the Vltava. Perfect for a first stroll after arrival.',
  prague_airport:
    "Václav Havel Airport is Prague's main gateway since 1937. Modern, compact and close to the city for a smooth arrival.",
  karlovy_vary:
    'A famed spa town known for hot springs, colonnades and the international film festival since 1946.',
  dresden:
    'Saxony’s baroque gem on the Elbe, rebuilt after WWII; the Frauenkirche and Zwinger are must-sees.',
  vienna_austria:
    'Imperial palaces meet coffeehouse culture. From Schönbrunn to the Ringstraße, Vienna blends history and style.',
  cesky_krumlov:
    'UNESCO-listed medieval town wrapped by the Vltava river, crowned by a magnificent castle.',
  plzeň:
    'Birthplace of Pilsner beer since 1842—cheers to brewery tours and lively squares.',
  liberec:
    'A northern city framed by the Jizera Mountains; the Ještěd Tower defines its skyline.',
  pardubice:
    'Charming East Bohemian city, famous for gingerbread and the Velká pardubická steeplechase.',
  hradec_kralove:
    'One of the Czech Republic’s oldest cities—wide boulevards, modernist gems and green riversides.',
  munich:
    'Bavarian capital of art, beer gardens and the English Garden—gateway to the Alps.',
  salzburg:
    'Mozart’s birthplace with baroque charm beneath the Hohensalzburg Fortress.',
  bratislava:
    'Riverside capital on the Danube—compact old town, hilltop castle and cozy cafes.',
  hallstatt:
    'Postcard-perfect Alpine village mirrored in the lake; ancient salt mining shaped its history.',
  graz: 'Austria’s design city—renaissance courtyards, the Schlossberg and bold modern architecture.',
  nuremberg:
    'Medieval walls and half-timbered houses alongside significant WWII-era history and museums.',
  regensburg:
    'UNESCO old town on the Danube—it thrived as a medieval trading hub.',
  linz: 'A creative city on the Danube with a vibrant cultural scene and the Ars Electronica Center.',
  krakow:
    'Poland’s former royal capital—Wawel Castle, Rynek Główny and the historic Kazimierz district.',
  budapest:
    'The Pearl of the Danube—thermal baths, grand boulevards and twin cities Buda and Pest.',
  wroclaw:
    'A lively university city spread across Odra islands, known for its bridges and dwarfs.',
  cesky_raj:
    'The Bohemian Paradise—sandstone rock towns, castles and panoramic trails.',
  kutna_hora:
    'Medieval silver town; St. Barbara’s Church and the nearby Sedlec Ossuary are iconic.',
  telc: 'UNESCO-listed square with colorful renaissance houses reflected in serene ponds.',
  děčín:
    'Gateway to Bohemian Switzerland—the Elbe canyon and sandstone cliffs start here.',
  ostrava:
    'Industrial heritage meets new culture—Dolní Vítkovice is a must for tech-history fans.',
};

const state = {
  car: 'standard', // standard|comfort|premium|minivan|premium-minivan
  currency: 'eur', // eur|czk
  query: '',
  currentPage: 1,
  itemsPerPage: 10,
};

const priceKey = (car, currency) => {
  switch (car) {
    case 'standard':
      return `standard_${currency}`;
    case 'comfort':
      return `comfort_${currency}`;
    case 'premium':
      return `premium_${currency}`;
    case 'minivan':
      return `minivan_${currency}`;
    case 'premium-minivan':
      return `premium_minivan_${currency}`;
    default:
      return `standard_${currency}`;
  }
};

async function loadData() {
  try {
    // Використовуємо локально імпортовані дані
    return pricesData;
  } catch (error) {
    console.error('❌ Помилка завантаження локальних даних:', error);
    throw error;
  }
}

function tCurrencySign(currency) {
  return currency === 'eur' ? '€' : 'Kč';
}

function getCarSpecs(carType) {
  const specs = {
    standard: { passengers: 4, childSeats: 2, luggage: 2 },
    comfort: { passengers: 4, childSeats: 2, luggage: 2 },
    premium: { passengers: 3, childSeats: 2, luggage: 2 },
    minivan: { passengers: 7, childSeats: 7, luggage: 7 },
    'premium-minivan': { passengers: 6, childSeats: 4, luggage: 4 },
  };
  return specs[carType] || specs.standard;
}

// Map route text to background image from /places
function getPlaceImage(route) {
  // 1) Витягуємо пункт призначення: частина після стрілки/дефіса/"to"
  const routeStr = String(route || '');
  const parts = routeStr.split(/—|–|-| to |→/i);
  const rawDest = (parts[1] || parts[0] || '').trim();
  // 2) Прибираємо вміст у дужках і нормалізуємо (діакритика, пробіли)
  const destClean = rawDest
    .replace(/\(.*?\)/g, '')
    .toLowerCase()
    .trim();

  const map = new Map([
    // Короткі синоніми
    ['prague airport', 'prague_airport'],
    ['airport', 'prague_airport'],
    ['city centre', 'city_centre'],
    ['city center', 'city_centre'],
    // Європейські міста
    ['vienna', 'vienna_austria'],
    ['bratislava', 'bratislava'],
    ['munich', 'munich'],
    ['nuremberg', 'nuremberg'],
    ['dresden', 'dresden'],
    ['salzburg', 'salzburg'],
    ['hallstatt', 'hallstatt'],
    ['linz', 'linz'],
    ['graz', 'graz'],
    ['krakow', 'krakow'],
    ['wroclaw', 'wroclaw'],
    ['budapest', 'budapest'],
    ['regensburg', 'regensburg'],
    // Чеські локації
    ['liberec', 'liberec'],
    ['ostrava', 'ostrava'],
    ['karlovy vary', 'karlovy_vary'],
    ['český krumlov', 'cesky_krumlov'],
    ['cesky krumlov', 'cesky_krumlov'],
    ['český ráj', 'cesky_raj'],
    ['cesky raj', 'cesky_raj'],
    ['hradec kralove', 'hradec_kralove'],
    ['kutna hora', 'kutna_hora'],
    ['telc', 'telc'],
    ['plzeň', 'plzeň'],
    ['plzen', 'plzeň'],
    ['děčín', 'děčín'],
    ['decin', 'děčín'],
    ['pardubice', 'pardubice'],
  ]);

  // Спочатку намагаємось знайти пряме включення у пункті призначення
  for (const [needle, file] of map.entries()) {
    if (destClean.includes(needle)) {
      return placeImagesByName[file] || placeImagesByName['city_centre'];
    }
  }

  // 3) Fallback: нормалізуємо до базового filename
  const basic = destClean
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, '_');
  if (basic && placeImagesByName[basic]) return placeImagesByName[basic];
  return placeImagesByName['city_centre'];
}

function render(listEl, data, lang) {
  const key = priceKey(state.car, state.currency);
  const q = state.query.trim().toLowerCase();

  const filtered = data
    .filter(r => !q || r.route.toLowerCase().includes(q))
    .filter(r => r[key] != null);

  // Pagination logic
  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / state.itemsPerPage);
  const startIndex = (state.currentPage - 1) * state.itemsPerPage;
  const endIndex = startIndex + state.itemsPerPage;
  const paginatedData = filtered.slice(startIndex, endIndex);

  const sign = tCurrencySign(state.currency);
  const t = common[lang] || common.en;
  const specs = getCarSpecs(state.car);
  const urlLang = lang === 'cs' ? 'cz' : lang;

  listEl.innerHTML = paginatedData
    .map(r => {
      const price = r[key];
      const bgSrc = getPlaceImage(r.route);
      const { title: destTitle, key: destKey } = parseDestination(r.route);
      return `
      <article class="price-card">
        <button class="price-card__info-btn" data-dest-key="${destKey}" data-dest-title="${destTitle}" data-img-src="${bgSrc}" aria-label="More about ${destTitle}">↗</button>
        <div class="price-card__bg" aria-hidden="true">
          <img src="${bgSrc}" alt="" width="300" height="200" />
          <div class="price-card__overlay"></div>
        </div>
        <div class="price-card__route">${r.route}</div>
        <div class="price-card__meta">
          <span class="price-card__value">${sign}${price}</span>
          <span class="price-card__badge">${t.price_per_car}</span>
        </div>
        <div class="price-card__specs">
          <span class="spec"><svg class="price-ico" aria-hidden="true"><use href="/img/svg/sprite.svg#person"></use></svg> ${specs.passengers} ${t.passengers}</span>
          <span class="spec"><svg class="price-ico" aria-hidden="true"><use href="/img/svg/sprite.svg#checkmark"></use></svg> ${specs.childSeats} ${t.child_seat}</span>
          <span class="spec"><svg class="price-ico" aria-hidden="true"><use href="/img/svg/sprite.svg#baggage"></use></svg> ${specs.luggage} ${t.luggage}</span>
        </div>
        <a class="button" href="/${urlLang}/#contacts" aria-label="${t.book} online">${t.book}</a>
      </article>
    `;
    })
    .join('');

  // Render pagination
  renderPagination(totalPages, totalItems, lang);
}

function renderPagination(totalPages, totalItems, lang) {
  const paginationEl = document.getElementById('pagination');
  if (!paginationEl) return;

  if (totalPages <= 1) {
    paginationEl.innerHTML = '';
    return;
  }
  const t = common[lang] || common.en;
  const startItem = (state.currentPage - 1) * state.itemsPerPage + 1;
  const endItem = Math.min(state.currentPage * state.itemsPerPage, totalItems);

  let paginationHTML = `
    <button class="pagination-btn" ${
      state.currentPage === 1 ? 'disabled' : ''
    } data-page="prev">
      ← ${t.previous || 'Previous'}
    </button>
  `;

  // Show page numbers (max 5 pages visible)
  const maxVisiblePages = 5;
  let startPage = Math.max(
    1,
    state.currentPage - Math.floor(maxVisiblePages / 2)
  );
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    paginationHTML += `
      <button class="pagination-btn ${
        i === state.currentPage ? 'is-active' : ''
      }" data-page="${i}">
        ${i}
      </button>
    `;
  }

  paginationHTML += `
    <button class="pagination-btn" ${
      state.currentPage === totalPages ? 'disabled' : ''
    } data-page="next">
      ${t.next || 'Next'} →
    </button>
    <div class="pagination-info">
      ${startItem}-${endItem} з ${totalItems}
    </div>
  `;

  paginationEl.innerHTML = paginationHTML;

  // Add event listeners
  paginationEl.querySelectorAll('.pagination-btn').forEach(btn => {
    btn.addEventListener('click', e => {
      const page = e.target.dataset.page;
      if (page === 'prev' && state.currentPage > 1) {
        state.currentPage--;
      } else if (page === 'next' && state.currentPage < totalPages) {
        state.currentPage++;
      } else if (!isNaN(page)) {
        state.currentPage = parseInt(page);
      }

      const listEl = document.getElementById('prices-list');
      const data = window.currentPricesData || [];
      render(listEl, data, lang);
    });
  });
}

function bindUI(data, lang) {
  const listEl = document.getElementById('prices-list');
  window.currentPricesData = data; // Store data globally for pagination

  document.querySelectorAll('.prices-toolbar .tab').forEach(btn => {
    btn.addEventListener('click', () => {
      document
        .querySelectorAll('.prices-toolbar .tab')
        .forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      state.car = btn.dataset.car;
      state.currentPage = 1; // Reset to first page
      render(listEl, data, lang);
    });
  });

  document.querySelectorAll('.currency-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document
        .querySelectorAll('.currency-btn')
        .forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      state.currency = btn.dataset.currency;
      state.currentPage = 1; // Reset to first page
      render(listEl, data, lang);
    });
  });

  const search = document.getElementById('prices-search');
  if (search) {
    search.addEventListener('input', () => {
      state.query = search.value;
      state.currentPage = 1; // Reset to first page
      render(listEl, data, lang);
    });
  }

  // Attach once: event delegation for info button
  if (!listEl.dataset.infoListenerAttached) {
    listEl.addEventListener('click', e => {
      const btn = e.target.closest('.price-card__info-btn');
      if (!btn) return;
      const destKey = btn.dataset.destKey;
      const destTitle = btn.dataset.destTitle;
      const imgSrc = btn.dataset.imgSrc;
      openPlaceModal({
        key: destKey,
        title: destTitle,
        img: imgSrc,
        opener: btn,
      });
    });
    listEl.dataset.infoListenerAttached = 'true';
  }

  render(listEl, data, lang);
}

(function init() {
  const root = document.documentElement;
  const lang = root.getAttribute('lang') || 'en';

  loadData()
    .then(data => {
      bindUI(data, lang);
    })
    .catch(err => {
      console.error('❌ Помилка завантаження даних:', err);

      // Показуємо помилку користувачу
      const listEl = document.getElementById('prices-list');
      if (listEl) {
        listEl.innerHTML = `
          <div style="padding: 20px; text-align: center; color: #dc2626;">
            <h3>Помилка завантаження даних</h3>
            <p>Не вдалося завантажити ціни. Спробуйте оновити сторінку.</p>
            <p>Помилка: ${err.message}</p>
          </div>
        `;
      }
    });
})();

// ---------- Modal logic ----------
let ensurePlaceModalPromise;
async function ensurePlaceModal() {
  let modal = document.getElementById('place-modal');
  if (modal) return modal;
  if (!ensurePlaceModalPromise) {
    ensurePlaceModalPromise = fetch('/partials/place-modal.html')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load modal HTML');
        return res.text();
      })
      .then(html => {
        const tpl = document.createElement('div');
        tpl.innerHTML = html.trim();
        const el = tpl.firstElementChild;
        document.body.appendChild(el);
        // Close on backdrop or button
        el.addEventListener('click', e => {
          if (e.target.matches('[data-close-modal]')) closePlaceModal();
        });
        // Close on Escape
        window.addEventListener('keydown', e => {
          if (e.key === 'Escape') closePlaceModal();
        });
        return el;
      })
      .catch(err => {
        console.error('❌ Failed to load place-modal.html:', err);
        // Fallback to inline creation if fetch fails
        const tpl = document.createElement('div');
        tpl.innerHTML = `
          <div id="place-modal" class="place-modal" hidden>
            <div class="place-modal__backdrop" data-close-modal></div>
            <div class="place-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="place-modal-title">
              <button class="place-modal__close" aria-label="Close" data-close-modal>×</button>
              <img class="place-modal__image" src="" alt="" width="400" height="300" />
              <h3 id="place-modal-title" class="place-modal__title"></h3>
              <p class="place-modal__text"></p>
            </div>
          </div>`;
        const el = tpl.firstElementChild;
        document.body.appendChild(el);
        el.addEventListener('click', e => {
          if (e.target.matches('[data-close-modal]')) closePlaceModal();
        });
        window.addEventListener('keydown', e => {
          if (e.key === 'Escape') closePlaceModal();
        });
        return el;
      });
  }
  return ensurePlaceModalPromise;
}

async function openPlaceModal({ key, title, img, opener }) {
  const modal = await ensurePlaceModal();
  const elImg = modal.querySelector('.place-modal__image');
  const elTitle = modal.querySelector('.place-modal__title');
  const elText = modal.querySelector('.place-modal__text');
  const btnClose = modal.querySelector('.place-modal__close');
  elImg.src = img;
  elImg.alt = title;
  elTitle.textContent = title;
  elText.textContent =
    storiesByKey[key] ||
    'A charming destination with its own unique story and atmosphere.';
  modal.hidden = false;
  modal._returnFocus = opener || document.activeElement;
  document.body.classList.add('modal-open');
  // trigger entrance animation
  requestAnimationFrame(() => modal.classList.add('is-open'));
  if (btnClose) btnClose.focus();
}

function closePlaceModal() {
  const modal = document.getElementById('place-modal');
  if (!modal) return;
  // play closing animation then hide
  modal.classList.remove('is-open');
  modal.classList.add('is-closing');
  const ANIM_MS = 230;
  window.setTimeout(() => {
    modal.hidden = true;
    modal.classList.remove('is-closing');
    document.body.classList.remove('modal-open');
    if (modal._returnFocus && typeof modal._returnFocus.focus === 'function') {
      try {
        modal._returnFocus.focus();
      } catch {}
    }
  }, ANIM_MS);
}
